import { AssetType } from '@ve-pro/enums/asset'
import { SegmentType } from '@ve-pro/enums/segment'
import type {
  SegmentAudio,
  SegmentCollection,
  SegmentVideo,
} from '@ve-pro/types/track.type'

// 淡入淡出的最大默认值
const FADE_DURATION_MAX = 10 * 1000 // 10s

/**
 * 是否支持淡入淡出
 * @param segment
 * @returns
 */
export function isSupportFade(
  segment: SegmentCollection,
): segment is SegmentAudio | SegmentVideo {
  return (
    segment.type === SegmentType.Audio ||
    (segment.type === SegmentType.Video &&
      segment.material.type === AssetType.Video)
  )
}

/**
 * 支持淡入淡出前提下，是否是可进行淡入淡出操作
 * @param segment
 * @returns
 */
export function isSupportAudioFade(segment: SegmentCollection): boolean {
  const type = segment.type
  if (type === SegmentType.Video && segment.material.type === AssetType.Video) {
    // 可分离音频，但还未分离的时候，可以展示音频面板
    return segment.audio_separatable && !segment.audio_separated
  } else if (type === SegmentType.Audio) {
    return true
  }

  return false
}

/**
 * 获取淡入的最大时长（ms）
 * @param segment
 * @returns 毫秒
 */
export function getSegmentFadeInMax(
  segment: SegmentVideo | SegmentAudio,
): number {
  return Math.min(
    FADE_DURATION_MAX,
    fixedSegmentFadeDuration(segment.duration - segment.fade_out_duration),
  )
}

/**
 * 获取淡出的最大时长（ms）
 * @param segment
 * @returns 毫秒
 */
export function getSegmentFadeOutMax(
  segment: SegmentVideo | SegmentAudio,
): number {
  return Math.min(
    FADE_DURATION_MAX,
    fixedSegmentFadeDuration(segment.duration - segment.fade_in_duration),
  )
}

export function fixedSegmentFadeDuration(
  duration: number,
  max?: number,
): number {
  return Math.min(max ?? Infinity, duration)
}

export function updateSegmentFadeDuration(
  segment: SegmentCollection,
  fixedPercent?: number,
): void {
  if (isSupportFade(segment)) {
    const fadePercent =
      fixedPercent ??
      segment.fade_in_duration /
        Math.floor(segment.fade_in_duration + segment.fade_out_duration)
    const fadeDuration = Math.floor(
      segment.fade_in_duration + segment.fade_out_duration,
    )
    if (fadeDuration > segment.duration) {
      if (segment.fade_in_duration === 0 && segment.fade_out_duration > 0) {
        segment.fade_out_duration = fixedSegmentFadeDuration(
          segment.duration,
          getSegmentFadeOutMax(segment),
        )
      } else if (
        segment.fade_out_duration === 0 &&
        segment.fade_in_duration > 0
      ) {
        segment.fade_in_duration = fixedSegmentFadeDuration(
          segment.duration,
          getSegmentFadeInMax(segment),
        )
      } else if (
        segment.fade_in_duration > 0 &&
        segment.fade_out_duration > 0
      ) {
        segment.fade_in_duration = fixedSegmentFadeDuration(
          segment.duration * fadePercent,
        )

        segment.fade_out_duration = fixedSegmentFadeDuration(
          segment.duration * (1 - fadePercent),
          getSegmentFadeOutMax(segment),
        )
      }
    }
    patchSegmentFadeDuration(segment)
  }
}

// 需求：剪辑到最小时长（0.1s)，再展开音频，此时淡入淡出数值为0s
export function patchSegmentFadeDuration(segment: SegmentCollection): void {
  if (segment.duration <= 100 && isSupportFade(segment)) {
    segment.fade_in_duration = 0
    segment.fade_out_duration = 0
  }
}
